import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'

/**
 * API client กลาง
 *
 * - baseURL = /api/v1
 * - withCredentials = true → ส่ง PHPSESSID + CSRF cookie
 * - interceptor:
 *     ทุก non-GET → bootstrap + แนบ X-CSRF-Token
 *     401 → ไป login (ยกเว้นกำลังอยู่หน้า login อยู่แล้ว)
 *     419 (CSRF mismatch) → รีเฟรช token + retry 1 ครั้ง
 *     อื่น ๆ → throw ApiError
 */

export interface ApiErrorField { field: string; message: string }

export interface ApiEnvelope<T = unknown> {
  success: boolean
  data: T | null
  error: { code: string; message: string; fields?: ApiErrorField[] } | null
}

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    /** structured per-field errors (envelope.error.fields[]) — Chunk Q */
    public fields?: ApiErrorField[],
  ) {
    super(message)
  }
}

export const api = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  headers: { Accept: 'application/json' },
  timeout: 30_000,
})

// ---- CSRF token bootstrap ---------------------------------------------------
let csrfTokenCache: string | null = null
let csrfFetchPromise: Promise<string> | null = null

async function fetchCsrf(): Promise<string> {
  if (csrfFetchPromise) return csrfFetchPromise
  // ใช้ axios instance เดียวกัน (api) ทับด้วย method GET → bypass CSRF interceptor
  // โดยอัตโนมัติ (interceptor skip GET) แต่ withCredentials ยังอยู่ → ส่ง PHPSESSID
  csrfFetchPromise = api
    .get<ApiEnvelope<{ token: string }>>('/csrf')
    .then((r) => {
      const t = r.data.data?.token ?? ''
      csrfTokenCache = t
      return t
    })
    .finally(() => { csrfFetchPromise = null })
  return csrfFetchPromise
}

async function ensureCsrf(): Promise<string> {
  if (csrfTokenCache) return csrfTokenCache
  return fetchCsrf()
}

/**
 * token ที่ cache ไว้ (sync) — สำหรับ fetch(keepalive) ตอน pagehide ที่รอ promise ไม่ได้
 * null = ยังไม่เคย bootstrap (request non-GET ก่อนหน้าจะทำให้มีเสมอ)
 */
export function getCsrfTokenSync(): string | null {
  return csrfTokenCache
}

// ---- request interceptor: แนบ token + ตั้งค่า config ---------------------
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const method = (config.method ?? 'get').toLowerCase()
  if (!['get', 'head', 'options'].includes(method)) {
    const t = await ensureCsrf()
    if (t) config.headers.set('X-CSRF-Token', t)
  }
  return config
})

// ---- response interceptor: 401 / 419 handling ----------------------------
api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError<ApiEnvelope> & { config?: InternalAxiosRequestConfig & { _csrfRetried?: boolean } }) => {
    const status = error.response?.status ?? 0
    const env = error.response?.data
    const code = env?.error?.code ?? 'network_error'
    const message = env?.error?.message ?? error.message ?? 'unknown error'

    // 419 = CSRF mismatch → invalidate cache + retry รอบเดียว
    if (status === 419 && error.config && !error.config._csrfRetried) {
      csrfTokenCache = null
      const t = await ensureCsrf()
      error.config._csrfRetried = true
      error.config.headers.set('X-CSRF-Token', t)
      return api.request(error.config)
    }

    // 401 → kick to login (เว้นกำลัง login อยู่)
    if (status === 401 && !error.config?.url?.includes('/auth/')) {
      if (location.pathname !== '/app/login') {
        location.href = '/app/login?reason=expired'
      }
    }

    return Promise.reject(new ApiError(code, message, status, env?.error?.fields))
  }
)

/** ปอกเปลือก envelope */
export async function unwrap<T>(p: Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  const { data } = await p
  if (!data.success || data.data === null) {
    throw new ApiError(
      data.error?.code ?? 'unknown',
      data.error?.message ?? 'unknown',
      0,
      data.error?.fields
    )
  }
  return data.data
}
